import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Routes, Route } from 'react-router-dom';
import { withRouterLegacy } from 'core/routerUtils';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import * as roomActions from 'features/room-builder/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import * as materialChainActions from 'features/material-chain/duck';
import {
  RoomInformation,
  RoomStructure,
  LegacyRoomStructure,
  LegacyRoomSeatingDetails,
} from 'features/room-builder/';
import { AlertMessage, PageNotFound, Spinner } from 'shared/components/';
import { ROOM_STATUS } from 'shared/constants';
import { mapRoomTypesById } from 'shared/selectors';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import debounce from 'lodash/debounce';
import 'features/room-builder/style.scss';
import './EventRoomsContainer.scss';

const getUrl = (stepUrl, eventId, roomId) =>
  `/test-event/${eventId}/rooms${stepUrl}${roomId ? `/${roomId}` : ''}`;

// STATEDIST-11174: how long field edits on the merged Structure & Seating step are
// debounced before triggering an autosave. No debounce utility exists elsewhere in this
// codebase; lodash (already a dependency, e.g. RoomStructure.jsx's isEmpty/omit usage) is
// reused here rather than adding a new package.
const STRUCTURE_AUTOSAVE_DEBOUNCE_MS = 800;

// STATEDIST-11174: the "Auto Saved" confirmation stays visible for exactly 5 seconds after
// the most recent successful autosave (AlertMessage's own default is 3000ms).
const AUTO_SAVED_MESSAGE_TIME_MS = 5000;

const defaultRoom = {
  name: '',
  locationEvent: {},
  capacity: '',
  timing: '',
  roomFacilities: [],
  shape: null,
  seating: null,
  deskType: null,
  supervisorDesk: null,
  status: 'inactive',
  seatingRowCount: 0,
  seatingColumnCount: 0,
  writingSurfaceWidth: 0,
  writingSurfaceDepth: 0,
  distanceBetweenShoulders: 0,
  distanceBetweenHeads: 0,
  singleRoomFlag: false,
};

const getLocationEventCapacity = (location, timingType) => {
  let sum = 0;
  if (location.locationEvent?.locationEventCapacities.length) {
    location.locationEvent.locationEventCapacities.forEach(
      (exam) => (sum += exam.capacityType.timing === timingType ? exam.capacity : 0)
    );
  }
  return sum;
};

const mapRooms = (locations, rooms) =>
  locations.map((location) => {
    const foundRooms = rooms.filter((room) => room.locationEvent.id === location.locationEvent.id);
    location.roomCapacity = 0;
    location.capacityStandard = getLocationEventCapacity(location, 'standard');
    location.capacityExtended = getLocationEventCapacity(location, 'extended');

    if (!foundRooms.length) {
      location.rooms = foundRooms;
      return location;
    }

    foundRooms.forEach((room) => (location.roomCapacity += room.capacity));
    location.rooms = foundRooms;
    return location;
  });

export class EventRoomsContainer extends Component {
  constructor(props) {
    super(props);

    const { params } = this.props.match;
    let step = this.getBuildSteps().findIndex((bStep) => bStep.url === `/${params.step}`);

    if (step === -1) {
      step = 0;
    }

    this.state = {
      locations: [],
      step,
      room: { ...defaultRoom },
      deleteRoom: null,
      alertMessage: '',
      errorDeleteRoom: false,
      showMessage: false,
      // Remounts <AlertMessage> on every successful autosave (even while already showing)
      // so its own internal setTimeout restarts from the latest success, per STATEDIST-11174
      // ("the message can keep extending indefinitely as long as changes keep landing
      // inside each 5-second window").
      autoSavedMessageKey: 0,
      // Set when completing Room Information fails to autosave-create the room (blocking
      // navigation to Structure & Seating, see nextStep) — surfaced on RoomInformation so
      // the coordinator isn't left with a Continue button that silently does nothing.
      infoAutosaveError: false,
    };

    // STATEDIST-11174: one autosave idempotency token per brand-new room draft, minted
    // lazily on the first autosave attempt (see getAutosaveToken) and resent unchanged for
    // every subsequent autosave call on that same draft. Lives only in this component's own
    // instance state — never persisted — and becomes irrelevant (but harmless to keep
    // sending) once the room has a real id.
    this.autosaveToken = null;

    this.removeMessage = this.removeMessage.bind(this);
    this.nextStep = this.nextStep.bind(this);
    this.prevStep = this.prevStep.bind(this);
    this.autosaveRoom = this.autosaveRoom.bind(this);
    this.onStructureFieldChange = this.onStructureFieldChange.bind(this);

    // STATEDIST-11174: debounced autosave for in-progress Structure & Seating field edits.
    // Declared per-instance (not module-level) so each mounted wizard has its own timer.
    //
    // Review fix (B3): `hadId` is captured by onStructureFieldChange at SCHEDULING time
    // (i.e. whether this.state.room already had a real id when the field change happened),
    // not at execution time. If the debounce fires only after nextStep() has already run
    // its final-step save() and reset this.state.room to a blank { ...defaultRoom } (no
    // id), a call that was scheduled while the room legitimately had an id would otherwise
    // read that reset, id-less room and call autosaveCreateRoom with the SAME idempotency
    // token as the room's original create — risking a stray duplicate draft or corrupting
    // the just-completed room. Bail out rather than reusing that token. A call scheduled
    // while the room never had an id (the normal first-ever create-on-type flow) is left
    // alone: `hadId` is false, so there is nothing stale to detect and the create proceeds
    // as intended. `nextStep` also cancels any pending call outright (see below) — this is
    // the belt-and-suspenders guard for a call that had already fired (is in-flight) at the
    // moment nextStep ran, which cancel() cannot stop.
    this.debouncedAutosaveStructure = debounce((fields, hadId) => {
      if (hadId && !this.state.room.id) {
        return;
      }
      this.autosaveRoom(fields);
    }, STRUCTURE_AUTOSAVE_DEBOUNCE_MS);
  }

  componentDidMount() {
    const { roomActions, staticActions, loadedOptions, tcEvent } = this.props;
    this.fetchData();
    staticActions.getConfigValues();

    if (!loadedOptions && !isEmpty(tcEvent)) {
      roomActions.getRoomOptions(tcEvent.testingEvent.adminId.cycleId.id);
    }
  }

  // Flag-consumption pattern per STATEDIST-11343 (see
  // StaffAndRoomsContainer.isScrollRestoreEnabled()): StaticService.getConfigValues() ->
  // staticDuck.js (GET_CONFIG_VALUES) -> state.statics.configValues -> looked up here by code.
  // Legacy (3-step) flow renders when the flag is off (default) or not yet loaded.
  isRoomSetupRedesignEnabled() {
    const entry = (this.props.configValues || []).find(
      (configValue) => configValue.code === 'room_setup_redesign_enabled'
    );
    return entry?.value === 'true';
  }

  getBuildSteps() {
    return this.isRoomSetupRedesignEnabled()
      ? [{ url: '/information' }, { url: '/structure' }]
      : [{ url: '/information' }, { url: '/structure' }, { url: '/seating-details' }];
  }

  // STATEDIST-11673: "is it test day or later" — wants "on or after" (stays true once test
  // day starts, never flips back), unlike shared/helpers.js's isEventDate/isWindowEvent
  // ("currently within the test day/window" only).
  //
  // Confirmed via a failing test (before this fix): comparing with plain moment(...) —
  // even with 'day' granularity, matching TcCurrentEvent.jsx's isBefore/isAfter(..., 'day')
  // calls on this exact field pairing — is NOT timezone-safe. serverDateTime is a full UTC
  // timestamp (a trailing "Z"); testDate/rescheduleDate is a bare "YYYY-MM-DD" with no time
  // or zone, which plain moment() parses as LOCAL midnight in the browser's own timezone.
  // 'day' granularity then resolves serverDateTime's day in that SAME local zone too — so a
  // server timestamp shortly after UTC midnight on test day (e.g. 00:01 UTC) can land on
  // the PREVIOUS calendar day for a browser west of UTC, comparing false against a testDate
  // that local midnight puts on the correct day. moment.utc(...) on both sides anchors the
  // comparison to testDate's actual calendar date regardless of the browser's timezone.
  // Fails safe to true (seat count required, today's behavior) when
  // tcEvent/serverDateTime/testDate aren't loaded yet.
  isTestDayOrLater() {
    const { tcEvent, serverDateTime } = this.props;
    const eventDate = tcEvent?.rescheduleDate || tcEvent?.testingEvent?.testDate;
    if (!serverDateTime || !eventDate) {
      return true;
    }
    return moment.utc(serverDateTime).isSameOrAfter(moment.utc(eventDate), 'day');
  }

  componentDidUpdate() {
    const { rooms, locations } = this.props;

    if (locations.length) {
      if (this.state.locations.length <= 0) {
        this.setState({
          locations: mapRooms(locations, rooms),
        });
      }
    }

    // if (room && !room.loading && params.roomId && isUndefined(this.state.room.id)) {
    //   this.setState({
    //     room,
    //   });
    // }
  }

  componentWillUnmount() {
    this.props.roomActions.clearPastEventsAction();
    this.debouncedAutosaveStructure.cancel();
  }

  fetchData = async () => {
    const { match, staticActions, roomActions, tcEventActions, location } = this.props;

    // Extract roomId from pathname since it's in the wildcard portion
    // Path format: /test-event/:eventId/rooms/:step/:roomId?
    const pathParts = location.pathname.split('/');
    const roomsIndex = pathParts.indexOf('rooms');
    const roomId = pathParts[roomsIndex + 2]; // step is at +1, roomId at +2

    if (roomId && !Number.isNaN(roomId)) {
      await roomActions.getRoom(roomId);
      const { room } = this.props;
      this.setState({
        room: room.id && !room.loading ? room : { ...defaultRoom },
      });
    }

    tcEventActions.getTcEvent(match.params.eventId).then(() => {
      staticActions.getRoomTypes(match.params.eventId);
      tcEventActions.getTestCenterCycle(match.params.eventId);
      roomActions.getRoomOptions(this.props.tcEvent.testingEvent.adminId.cycleId.id);
    });

    tcEventActions.getRooms(match.params.eventId);
    tcEventActions.getRoomsDTO(match.params.eventId);
  };

  removeMessage() {
    this.setState({
      showMessage: false,
    });
  }

  // STATEDIST-11174: mint one autosave idempotency token per brand-new draft, lazily, before
  // its first autosave call — and never regenerate it afterwards. Irrelevant (but harmless
  // to keep sending) once the room has a real id, since later updates are keyed by that id.
  getAutosaveToken() {
    if (!this.autosaveToken) {
      this.autosaveToken =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `autosave-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    return this.autosaveToken;
  }

  // STATEDIST-11174: autosave `fields` merged onto the current room via the draft/
  // autosaveToken-aware contract (STATEDIST-11173, backend) — creating the room on its very
  // first autosave (no id yet) and updating it by id thereafter. On success, merges the
  // saved room (picking up a freshly-assigned id) into local state and shows/restarts the
  // "Auto Saved" confirmation. On failure, silently does nothing: no error is ever surfaced
  // to the coordinator, and whatever they've typed stays exactly as it is in local state.
  async autosaveRoom(fields) {
    const room = { ...this.state.room, ...fields };
    const token = this.getAutosaveToken();
    const result = room.id
      ? await this.props.roomActions.autosaveUpdateRoom(room, token)
      : await this.props.roomActions.autosaveCreateRoom(room, token);

    if (!result || !result.success) {
      return null;
    }

    const savedRoom = { ...room, ...result.room };
    this.setState((prevState) => ({
      room: savedRoom,
      showMessage: true,
      alertMessage: 'roomBuilder.autoSavedMessage',
      // Remounting <AlertMessage> (via this key, see render()) restarts its own internal
      // auto-dismiss timer from *this* success, even if it was already showing — the 5s
      // window keeps extending as long as autosaves keep landing inside it.
      autoSavedMessageKey: prevState.autoSavedMessageKey + 1,
    }));
    return savedRoom;
  }

  // STATEDIST-11174: debounced onChange hook wired to the merged Structure & Seating step
  // (RoomStructure's onFieldChange prop) — no-op when the flag is off, so the legacy 3-step
  // flow never autosaves.
  onStructureFieldChange(fields) {
    if (!this.isRoomSetupRedesignEnabled()) {
      return;
    }
    // STATEDIST-11174 (review B3): captured now, at scheduling time, not read fresh when
    // the debounced call eventually fires — see debouncedAutosaveStructure above.
    const hadId = !!this.state.room.id;
    this.debouncedAutosaveStructure(fields, hadId);
  }

  async nextStep(info, flag) {
    // STATEDIST-11174 (review B3): cancel any pending debounced Structure & Seating
    // autosave before the final save() below runs. Without this, a pending debounce could
    // fire during the awaited final save (a concurrent draft: true PUT against the same
    // room racing the authoritative save), or — worse — fire after this.state.room has
    // already been reset to a blank { ...defaultRoom } a few lines down, reusing the
    // just-completed room's idempotency token against an empty, id-less payload.
    this.debouncedAutosaveStructure.cancel();

    const { testCenterCycle } = this.props;

    let next = flag ? 1 : this.state.step + 1;
    let nextStep = testCenterCycle.cycle.seatingChartRequiredFlag ? this.getBuildSteps()[next] : null;
    const stateRoom = this.state.room;
    let room;

    if (info) {
      room =
        !isEmpty(stateRoom) && info.id && stateRoom.id && info.id !== stateRoom.id
          ? { ...info }
          : { ...stateRoom, ...info };
    } else {
      room = { ...defaultRoom };
    }

    const omitLastStep = room.status !== ROOM_STATUS.inactive && next === 2;

    if (!nextStep || omitLastStep) {
      //  last step
      //  save info and re-direct to room list
      const stepsBack = next;
      const roomSaved = await this.save(room);
      if (!roomSaved) {
        return;
      }
      this.setState({
        room: { ...defaultRoom },
      });

      // We need to listen for the popstate event because history.go() is asynchronous
      // and we want to replace the state after the navigation
      globalThis.addEventListener(
        'popstate',
        () => {
          this.props.history.replace(
            `/test-event/${this.props.match.params.eventId}/staff-and-rooms`,
            {
              successNotif: this.props.t(
                `${room.id ? 'roomBuilder.successEditRoom' : 'roomBuilder.successCreateRoom'}`
              ),
              editedRoomId: room.id || null,
            },
            // Without this, React Router's <ScrollRestoration /> (STATEDIST-11193) treats
            // this replace as a fresh navigation and resets scroll to top, fighting our own
            // restore below.
            { preventScrollReset: true }
          );
        },
        {
          once: true,
        }
      );
      this.props.history.go(-stepsBack);
      return;
    }

    // STATEDIST-11174: completing Room Information (the only non-final Continue in the
    // redesigned 2-step flow) autosaves it — creating the room here if it doesn't already
    // have an id — so nothing is lost if the coordinator closes the tab before reaching
    // Structure & Seating. Awaited so both the pushed URL and local state carry the real
    // assigned id. Flag off (legacy 3-step flow): isRoomSetupRedesignEnabled() is false, so
    // this never runs and the legacy flow has zero autosave, exactly as today.
    //
    // Unlike the Structure step's background debounced autosave (which must stay silent
    // per STATEDIST-11174's business requirement), this is the room's very first creation —
    // Structure has nothing to attach to without it. Navigating away with no id lets the
    // browser reach `/rooms/structure` (no roomId in the URL), and a page refresh there
    // reinitializes this.state.room back to the blank defaultRoom (including an empty
    // locationEvent), silently corrupting the next autosave. Block navigation and surface
    // the failure instead — mirroring the existing `if (!roomSaved) return;` pattern the
    // final-step save (above) already uses for the exact same reason.
    if (info && this.isRoomSetupRedesignEnabled()) {
      const savedRoom = await this.autosaveRoom(room);
      if (!savedRoom) {
        // Still persist the locally-merged room (name, locationEvent, etc. as just entered
        // on Information) — only the id-granting create failed, not the coordinator's
        // entries. Retrying Continue re-submits the same info and tries again.
        this.setState({ room, infoAutosaveError: true });
        return;
      }
      room = savedRoom;
    }

    this.setState({
      step: next,
      room,
      infoAutosaveError: false,
    });
    this.props.history.push(
      getUrl(nextStep.url, this.props.match.params.eventId, next ? room.id : null)
    );
  }

  async save(room) {
    if (room.id) {
      await this.props.roomActions.updateRoom(room);
    } else {
      await this.props.roomActions.createRoom(room);
    }
    if (isEmpty(this.props.errorRoom)) {
      this.props.tcEventActions.getRooms(this.props.tcEvent.id);
      return true;
    }
    return false;
  }

  prevStep() {
    // STATEDIST-11174 (review B-Back / same family as B3): cancel any pending debounced
    // Structure & Seating autosave before navigating back. componentWillUnmount (where
    // cancel() also runs) never fires here — EventRoomsContainer stays mounted across its
    // own internal step routing on Back — so without this, a debounce scheduled from a
    // Structure field edit can still fire ~800ms later after the coordinator has already
    // navigated back and changed this.state.room further (e.g. re-editing Information),
    // unconditionally overwriting that newer state with the stale autosaved room. The
    // `hadId`-capture-at-scheduling-time guard inside debouncedAutosaveStructure's callback
    // (added for B3) doesn't help here: it only bails when this.state.room.id has been
    // reset to empty by nextStep's final-step save, which never happens on this path —
    // Back just navigates within the same in-progress, id-bearing room. cancel() is the
    // actual fix, mirroring nextStep() exactly.
    this.debouncedAutosaveStructure.cancel();

    if (this.state.step > 0) {
      const prevStep = this.state.step - 1;
      const room = this.state.room;
      this.setState({
        step: prevStep,
        room,
      });
    } else {
      const room = this.state.room;
      // We need to listen for the popstate event because history.goBack() is asynchronous
      // and we want to replace the state after the navigation
      globalThis.addEventListener(
        'popstate',
        () => {
          this.props.history.replace(
            `/test-event/${this.props.match.params.eventId}/staff-and-rooms`,
            {
              editedRoomId: room.id || null,
            },
            // Without this, React Router's <ScrollRestoration /> (STATEDIST-11193) treats
            // this replace as a fresh navigation and resets scroll to top, fighting our own
            // restore below.
            { preventScrollReset: true }
          );
        },
        {
          once: true,
        }
      );
    }
    this.props.history.goBack();
    this.props.roomActions.resetErrorRoomAction();
  }

  render() {
    const redesignEnabled = this.isRoomSetupRedesignEnabled();

    return (
      <section className="room-builder">
        <div className="mt-3">
          <Routes>
            <Route path="information/:roomId?" element={<InformationView parent={this} />} />
            {redesignEnabled ? (
              <Route path="structure/:roomId?" element={<StructureView parent={this} />} />
            ) : (
              [
                <Route
                  key="structure"
                  path="structure/:roomId?"
                  element={<LegacyStructureView parent={this} />}
                />,
                <Route
                  key="seating-details"
                  path="seating-details/:roomId?"
                  element={<SeatingDetailsView parent={this} />}
                />,
              ]
            )}
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </div>
        <AlertMessage
          // STATEDIST-11174: key forces a remount (and thus a fresh internal setTimeout)
          // on every successful autosave, so the 5s auto-dismiss window restarts from the
          // latest success rather than expiring on the original timer.
          key={this.state.autoSavedMessageKey}
          show={this.state.showMessage}
          removeMessage={this.removeMessage}
          message={this.state.alertMessage}
          time={AUTO_SAVED_MESSAGE_TIME_MS}
          ariaLive="polite"
        />
      </section>
    );
  }
}

// Helper components to access route params and render views
const InformationView = withRouterLegacy(({ parent }) => {
  const {
    roomActions,
    loadedOptions,
    testCenterCycle,
    tcEvent,
    roomTypes,
    roomsDTO,
    roomTypesById,
    roomMaterials,
    roomOptions,
    materialChainActions,
    roomNameExistent,
    loadingRoomTypes,
    mappedRoomTiming,
    room: propsRoom,
  } = parent.props;
  const { locations, room, infoAutosaveError } = parent.state;

  const roomDTO = find(roomsDTO, { id: room.id });

  return loadedOptions &&
    !!locations.length &&
    !loadingRoomTypes &&
    !propsRoom.loading &&
    !isEmpty(testCenterCycle) ? (
    <RoomInformation
      roomOptions={roomOptions}
      roomTypesById={roomTypesById}
      locations={locations}
      tc={testCenterCycle.testCenterProgram.testCenter}
      tcEvent={tcEvent}
      room={room}
      manualTimerFlag={roomDTO ? roomDTO.manualTimerFlag : false}
      roomTypes={roomTypes}
      countMaterials={roomMaterials.length}
      onContinue={parent.nextStep}
      checkRoomNameExistence={roomActions.checkRoomNameExistence}
      checkRoomMismatch={roomActions.checkRoomMismatch}
      roomNameExistent={roomNameExistent}
      onBack={parent.prevStep}
      getRoomMaterials={materialChainActions.getRoomMaterials}
      resetRoomMaterials={materialChainActions.resetRoomMaterials}
      mappedRoomTiming={mappedRoomTiming}
      autosaveError={infoAutosaveError}
    />
  ) : (
    <Spinner />
  );
});

// New merged Room Structure + Seating Details screen (STATEDIST-11172, flag on).
const StructureView = withRouterLegacy(({ parent }) => {
  const { loadedOptions, roomOptions, errorRoom } = parent.props;
  const { room } = parent.state;

  return (
    loadedOptions && (
      <RoomStructure
        roomOptions={roomOptions}
        room={room}
        errorRoom={errorRoom}
        onContinue={parent.nextStep}
        onBack={parent.prevStep}
        // STATEDIST-11174: debounced autosave hook — legacy structure view (below) does not
        // receive this prop, so the flag-off 3-step flow has zero autosave.
        onFieldChange={parent.onStructureFieldChange}
        // STATEDIST-11673: legacy structure view (below) doesn't receive this — the legacy
        // 3-step flow's seat count requirement is unaffected by this ticket.
        isTestDayOrLater={parent.isTestDayOrLater()}
      />
    )
  );
});

// Legacy Room Structure step (flag off) — preserved exactly as it worked before the redesign.
const LegacyStructureView = withRouterLegacy(({ parent }) => {
  const { loadedOptions, roomOptions, errorRoom } = parent.props;
  const { room } = parent.state;

  return (
    loadedOptions && (
      <LegacyRoomStructure
        roomOptions={roomOptions}
        room={room}
        errorRoom={errorRoom}
        onContinue={parent.nextStep}
        onBack={parent.prevStep}
      />
    )
  );
});

// Legacy Seating Details step (flag off) — preserved exactly as it worked before the redesign.
const SeatingDetailsView = withRouterLegacy(({ parent }) => {
  const { locations, errorRoom } = parent.props;
  const { room } = parent.state;

  return (
    <LegacyRoomSeatingDetails
      room={room}
      locations={locations}
      errorRoom={errorRoom}
      onContinue={parent.nextStep}
      onBack={parent.prevStep}
    />
  );
});

function mapStateToProps(state) {
  return {
    testCenterCycle: state.tcEvent.testCenterCycle || {},
    user: state.users.currentUser || {},
    tcEvent: state.tcEvent.currentEvent || {},
    rooms: state.tcEvent.rooms || [],
    loadingRooms: state.tcEvent.loadingRooms,
    roomTypes: state.statics.roomTypes,
    mappedRoomTiming: state.statics.mappedRoomTiming,
    loadingRoomTypes: state.statics.loadingRoomTypes,
    roomOptions: state.roomOptions.options,
    loadingOptions: state.roomOptions.loadingOptions,
    loadedOptions: state.roomOptions.loadedOptions,
    errorRoomOptions: state.roomOptions.errorOptions || {},
    room: state.roomOptions.room || {},
    errorRoom: state.roomOptions.errorRoom || {},
    locations: state.tcEvent.eventLocations || [],
    allLocations: state.tcEvent.locations || [],
    roomsDTO: state.tcEvent.roomsDTO,
    roomTypesById: mapRoomTypesById(state),
    roomMaterials: state.materialChain.roomMaterials,
    loadedPastEvents: state.roomOptions.loadedPastEvents,
    loadingPastEvents: state.roomOptions.loadingPastEvents,
    errorPastEvents: state.roomOptions.errorPastEvents,
    errorCopyRooms: state.roomOptions.errorCopyRooms,
    roomNameExistent: state.roomOptions.roomNameExistent,
    configValues: state.statics.configValues,
    serverDateTime: state.statics.serverDateTime,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    tcEventActions: bindActionCreators(tcEventActions, dispatch),
    roomActions: bindActionCreators(roomActions, dispatch),
    staticActions: bindActionCreators(staticActions, dispatch),
    materialChainActions: bindActionCreators(materialChainActions, dispatch),
  };
}

export default withTranslation()(
  withRouterLegacy(connect(mapStateToProps, mapDispatchToProps)(EventRoomsContainer))
);

EventRoomsContainer.propTypes = {
  rooms: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  locations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  room: PropTypes.shape({
    id: PropTypes.number,
    loading: PropTypes.bool,
  }).isRequired,
  tcEvent: PropTypes.shape({
    id: PropTypes.number,
    studyFlag: PropTypes.bool,
    rescheduleDate: PropTypes.string,
    testingEvent: PropTypes.shape({
      testDate: PropTypes.string,
      adminId: PropTypes.shape({
        cycleId: PropTypes.shape({
          id: PropTypes.number,
        }),
      }),
    }),
  }).isRequired,
  serverDateTime: PropTypes.string,
  testCenterCycle: PropTypes.shape({
    cycle: PropTypes.shape({
      seatingChartRequiredFlag: PropTypes.bool,
    }),
    testCenterProgram: PropTypes.shape({
      testCenter: PropTypes.shape({}),
    }),
  }).isRequired,
  loadingRoomTypes: PropTypes.bool,
  roomTypes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  mappedRoomTiming: PropTypes.shape({}).isRequired,
  roomOptions: PropTypes.shape({}).isRequired,
  loadedOptions: PropTypes.bool,
  errorRoom: PropTypes.shape({}),
  roomsDTO: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      manualTimerFlag: PropTypes.bool,
    })
  ),
  roomTypesById: PropTypes.shape({}),
  roomMaterials: PropTypes.arrayOf(PropTypes.shape({})),
  roomNameExistent: PropTypes.bool,
  configValues: PropTypes.arrayOf(PropTypes.shape({})),
  history: PropTypes.shape({
    go: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      eventId: PropTypes.string,
      step: PropTypes.string,
    }),
  }).isRequired,
  t: PropTypes.func.isRequired,
  tcEventActions: PropTypes.shape({
    getTcEvent: PropTypes.func.isRequired,
    getRooms: PropTypes.func.isRequired,
    getTestCenterCycle: PropTypes.func.isRequired,
    getRoomsDTO: PropTypes.func.isRequired,
  }).isRequired,
  roomActions: PropTypes.shape({
    getRoomOptions: PropTypes.func.isRequired,
    getRoom: PropTypes.func.isRequired,
    createRoom: PropTypes.func.isRequired,
    updateRoom: PropTypes.func.isRequired,
    autosaveCreateRoom: PropTypes.func.isRequired,
    autosaveUpdateRoom: PropTypes.func.isRequired,
    resetErrorRoomAction: PropTypes.func.isRequired,
    clearPastEventsAction: PropTypes.func.isRequired,
    checkRoomNameExistence: PropTypes.func.isRequired,
    checkRoomMismatch: PropTypes.func.isRequired,
  }).isRequired,
  staticActions: PropTypes.shape({
    getRoomTypes: PropTypes.func.isRequired,
    getConfigValues: PropTypes.func.isRequired,
  }).isRequired,
  materialChainActions: PropTypes.shape({
    getRoomMaterials: PropTypes.func.isRequired,
    resetRoomMaterials: PropTypes.func.isRequired,
  }).isRequired,
};
